// src/Dashboard/pages/user/DashboardUser/components/ContactFormPanel.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaCheck, FaCheckDouble, FaCopy, FaEye, FaTrash } from "react-icons/fa";
import { MdMessage } from "react-icons/md";
import { toast } from "react-toastify";

import {
  deleteMessageContactForm,
  getAllMessagesContactForm,
  markMessageContactForm,
} from "../../../../../services/contactformServices";
import { useContactForm } from "../../../../../hooks/ContactFormContext";

import DashboardDrawer from "./DashboardDrawer";
import EmptyState from "./EmptyState";
import {
  formatDateTimeFR,
  isReadFlag,
  sortByDateDesc,
} from "../utils/dashboardUserUtils";

export default function ContactFormPanel({ userId, onCounts }) {
  const { setUnreadCountContactForm } = useContactForm();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const prevCountsRef = useRef({ total: -1, unread: -1 });

  const computeCounts = useCallback((list) => {
    const total = list.length;
    const unread = list.reduce(
      (acc, message) => acc + (isReadFlag(message.read_message) ? 0 : 1),
      0
    );

    return { total, unread };
  }, []);

  const pushCounts = useCallback(
    (list) => {
      const next = computeCounts(list);
      const prev = prevCountsRef.current;

      if (prev.total !== next.total || prev.unread !== next.unread) {
        prevCountsRef.current = next;
        onCounts?.(next);
        setUnreadCountContactForm?.(next.unread);
      }
    },
    [computeCounts, onCounts, setUnreadCountContactForm]
  );

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getAllMessagesContactForm(userId);
      const list = sortByDateDesc(
        Array.isArray(data) ? data : [],
        "created_at"
      );

      setMessages(list);
      pushCounts(list);
    } catch (error) {
      console.error("[ContactFormPanel] load error:", error);
      toast.error("Impossible de charger les messages du formulaire.");
    } finally {
      setLoading(false);
    }
  }, [userId, pushCounts]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () => computeCounts(messages),
    [messages, computeCounts]
  );

  const copyEmail = async (email) => {
    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
      toast.success("Adresse email copiée.");
    } catch (error) {
      console.error("[ContactFormPanel] copy email error:", error);
      toast.error("Impossible de copier l’adresse email.");
    }
  };

  const toggleReadStatus = async (messageId) => {
    const currentMessage = messages.find((message) => message.id === messageId);

    if (!currentMessage) return;

    const nextReadValue = !isReadFlag(currentMessage.read_message);

    try {
      await markMessageContactForm(messageId);

      const next = messages.map((message) =>
        message.id === messageId
          ? {
              ...message,
              read_message: nextReadValue,
            }
          : message
      );

      setMessages(next);
      pushCounts(next);

      setSelectedMessage((prev) =>
        prev?.id === messageId
          ? {
              ...prev,
              read_message: nextReadValue,
            }
          : prev
      );

      toast.success(
        nextReadValue
          ? "Message marqué comme lu."
          : "Message repassé en non lu."
      );
    } catch (error) {
      console.error("[ContactFormPanel] toggle read status error:", error);
      toast.error("Impossible de modifier l’état de lecture.");
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Supprimer ce message de contact ?")) return;

    try {
      await deleteMessageContactForm(messageId);

      const next = messages.filter((message) => message.id !== messageId);

      setMessages(next);
      pushCounts(next);

      setSelectedMessage((prev) => (prev?.id === messageId ? null : prev));

      toast.success("Message supprimé.");
    } catch (error) {
      console.error("[ContactFormPanel] delete error:", error);
      toast.error("Impossible de supprimer ce message.");
    }
  };

  const markAllRead = async () => {
    try {
      const unreadIds = messages
        .filter((message) => !isReadFlag(message.read_message))
        .map((message) => message.id);

      if (!unreadIds.length) {
        toast.info("Tous les messages sont déjà lus.");
        return;
      }

      await Promise.all(unreadIds.map((id) => markMessageContactForm(id)));

      const next = messages.map((message) => ({
        ...message,
        read_message: true,
      }));

      setMessages(next);
      pushCounts(next);

      setSelectedMessage((prev) =>
        prev ? { ...prev, read_message: true } : prev
      );

      toast.success("Tous les messages ont été marqués comme lus.");
    } catch (error) {
      console.error("[ContactFormPanel] mark all read error:", error);
      toast.error("Impossible de tout marquer comme lu.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!messages.length) {
    return (
      <EmptyState
        icon={MdMessage}
        title="Aucun message de contact"
        description="Les demandes envoyées depuis le formulaire du site apparaîtront ici."
      />
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-bold text-slate-900 dark:text-white">
            {counts.unread}
          </span>{" "}
          message{counts.unread > 1 ? "s" : ""} non lu
          {counts.unread > 1 ? "s" : ""} sur {counts.total}.
        </p>

        <button
          type="button"
          onClick={markAllRead}
          disabled={counts.unread === 0}
          className={[
            "inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition",
            counts.unread === 0
              ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-600"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
          ].join(" ")}
        >
          <FaCheckDouble />
          Tout marquer lu
        </button>
      </div>

      <div className="space-y-3">
        {messages.map((message) => {
          const unread = !isReadFlag(message.read_message);
          const { date, time } = formatDateTimeFR(
            message.created_at || message.createdAt
          );

          return (
            <article
              key={message.id}
              className={[
                "rounded-2xl border p-4 shadow-sm transition",
                unread
                  ? "border-sky-200 bg-sky-50/70 dark:border-sky-800/60 dark:bg-sky-900/20"
                  : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
              ].join(" ")}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {unread ? (
                      <span className="rounded-full bg-sky-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Non lu
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                        Lu
                      </span>
                    )}

                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {date} {time && `• ${time}`}
                    </span>
                  </div>

                  <h3 className="mt-2 font-bold text-slate-900 dark:text-white">
                    {message.name || "Expéditeur non renseigné"}
                  </h3>

                  {message.email && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">
                        {message.email}
                      </p>

                      <button
                        type="button"
                        onClick={() => copyEmail(message.email)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-violet-100 bg-violet-50 text-violet-700 transition hover:bg-violet-100 dark:border-violet-800/60 dark:bg-violet-900/20 dark:text-violet-200 dark:hover:bg-violet-900/40"
                        title="Copier l’adresse email"
                        aria-label="Copier l’adresse email"
                      >
                        <FaCopy className="text-xs" />
                      </button>
                    </div>
                  )}

                  <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {message.message || "Aucun contenu."}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => openMessage(message)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    title="Consulter"
                    aria-label="Consulter le message"
                  >
                    <FaEye />
                    Voir
                  </button>

                  <button
                    type="button"
                    onClick={() => toggleReadStatus(message.id)}
                    className={[
                      "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition",
                      unread
                        ? "bg-emerald-600 text-white hover:bg-emerald-700"
                        : "bg-sky-50 text-sky-700 hover:bg-sky-100 dark:bg-sky-900/20 dark:text-sky-200 dark:hover:bg-sky-900/40",
                    ].join(" ")}
                    title={unread ? "Marquer comme lu" : "Repasser en non lu"}
                    aria-label={
                      unread ? "Marquer comme lu" : "Repasser en non lu"
                    }
                  >
                    {unread ? <FaCheck /> : <MdMessage />}
                    {unread ? "Marquer lu" : "Repasser non lu"}
                  </button>

                  <button
                    type="button"
                    onClick={() => deleteMessage(message.id)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40"
                    title="Supprimer"
                    aria-label="Supprimer le message"
                  >
                    <FaTrash />
                    Supprimer
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <DashboardDrawer
        open={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        title={selectedMessage?.name || "Message de contact"}
        subtitle={selectedMessage?.email || ""}
        size="md"
      >
        {selectedMessage && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-900">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Coordonnées
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {!isReadFlag(selectedMessage.read_message) ? (
                      <span className="rounded-full bg-sky-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Non lu
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                        Lu
                      </span>
                    )}
                  </div>

                  <p className="mt-2 font-bold text-slate-900 dark:text-white">
                    {selectedMessage.name || "—"}
                  </p>

                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <p className="text-sm text-violet-700 dark:text-violet-300">
                      {selectedMessage.email || "—"}
                    </p>

                    {selectedMessage.email && (
                      <button
                        type="button"
                        onClick={() => copyEmail(selectedMessage.email)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-violet-100 bg-violet-50 text-violet-700 transition hover:bg-violet-100 dark:border-violet-800/60 dark:bg-violet-900/20 dark:text-violet-200 dark:hover:bg-violet-900/40"
                        title="Copier l’adresse email"
                        aria-label="Copier l’adresse email"
                      >
                        <FaCopy className="text-xs" />
                      </button>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => toggleReadStatus(selectedMessage.id)}
                  className={[
                    "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition",
                    !isReadFlag(selectedMessage.read_message)
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "bg-sky-50 text-sky-700 hover:bg-sky-100 dark:bg-sky-900/20 dark:text-sky-200 dark:hover:bg-sky-900/40",
                  ].join(" ")}
                >
                  {!isReadFlag(selectedMessage.read_message) ? (
                    <>
                      <FaCheck />
                      Marquer lu
                    </>
                  ) : (
                    <>
                      <MdMessage />
                      Repasser non lu
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                Message
              </p>

              <p className="whitespace-pre-line text-sm leading-7 text-slate-700 dark:text-slate-300">
                {selectedMessage.message || "Aucun contenu."}
              </p>
            </div>

            <div className="flex flex-col gap-2 border-t border-slate-200 pt-4 dark:border-slate-800 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => toggleReadStatus(selectedMessage.id)}
                className={[
                  "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition",
                  !isReadFlag(selectedMessage.read_message)
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "bg-sky-50 text-sky-700 hover:bg-sky-100 dark:bg-sky-900/20 dark:text-sky-200 dark:hover:bg-sky-900/40",
                ].join(" ")}
              >
                {!isReadFlag(selectedMessage.read_message) ? (
                  <>
                    <FaCheck />
                    Marquer comme lu
                  </>
                ) : (
                  <>
                    <MdMessage />
                    Repasser en non lu
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => deleteMessage(selectedMessage.id)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40"
              >
                <FaTrash />
                Supprimer
              </button>
            </div>
          </div>
        )}
      </DashboardDrawer>
    </>
  );
}